/**
 * Directivas v-longpress / v-press.
 * El estado de long-press es por elemento (WeakMap), no global,
 * para evitar carreras entre tarjetas del virtual-list.
 */
const TOUCH_MOVE_TOLERANCE = 6
const TOUCH_MOVE_BLOCK_DURATION = 700

function pressAndLongPress(Vue, options = { longPressDuration: 1000 }) {
    /** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
    const longPressedElements = new WeakMap()
    /** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
    const touchMovedElements = new WeakMap()

    const markLongPressed = (el) => {
        const existing = longPressedElements.get(el)
        if (existing) {
            clearTimeout(existing)
        }
        // Si no llega el click sintético, limpiar para no tragar el siguiente press
        const timeoutId = setTimeout(() => {
            longPressedElements.delete(el)
        }, 500)
        longPressedElements.set(el, timeoutId)
    }

    const consumeLongPressed = (el) => {
        if (!longPressedElements.has(el)) {
            return false
        }
        const timeoutId = longPressedElements.get(el)
        if (timeoutId) {
            clearTimeout(timeoutId)
        }
        longPressedElements.delete(el)
        return true
    }

    const markTouchMoved = (el) => {
        const existing = touchMovedElements.get(el)
        if (existing) {
            clearTimeout(existing)
        }
        const timeoutId = setTimeout(() => {
            touchMovedElements.delete(el)
        }, TOUCH_MOVE_BLOCK_DURATION)
        touchMovedElements.set(el, timeoutId)
    }

    const consumeTouchMoved = (el) => {
        if (!touchMovedElements.has(el)) {
            return false
        }
        clearTimeout(touchMovedElements.get(el))
        touchMovedElements.delete(el)
        return true
    }

    Vue.directive('longpress', {
        bind: function(el, binding) {
            const directiveOptions = typeof binding.value === 'function' ? { handler: binding.value } : binding.value
            const handler = directiveOptions.handler
            const shouldCancelOnTouchMove = Boolean(directiveOptions.cancelOnTouchMove)
            const longPressDuration = directiveOptions.longPressDuration || options.longPressDuration
            let pressTimer = null
            let touchStartPosition = null

            const start = (e) => {
                if (e.type === 'click' && e.button !== 0) {
                    return
                }

                if (shouldCancelOnTouchMove && e.type === 'touchstart' && e.touches.length > 0) {
                    const touch = e.touches[0]
                    touchStartPosition = { x: touch.clientX, y: touch.clientY }
                }

                if (pressTimer === null) {
                    pressTimer = setTimeout(() => {
                        markLongPressed(el)
                        handler(e)
                    }, longPressDuration)
                }
            }

            const cancel = () => {
                if (pressTimer !== null) {
                    clearTimeout(pressTimer)
                    pressTimer = null
                }
                touchStartPosition = null
            }

            const cancelOnTouchMove = (e) => {
                if (!touchStartPosition || e.touches.length === 0) {
                    return
                }

                const touch = e.touches[0]
                const movedHorizontally = Math.abs(touch.clientX - touchStartPosition.x)
                const movedVertically = Math.abs(touch.clientY - touchStartPosition.y)

                if (movedHorizontally > TOUCH_MOVE_TOLERANCE || movedVertically > TOUCH_MOVE_TOLERANCE) {
                    markTouchMoved(el)
                    cancel()
                }
            }

            // Evita el menú contextual nativo (desktop/touch long-press)
            // para no cancelar la selección múltiple.
            const preventContextMenu = (e) => {
                e.preventDefault()
            }

            el.addEventListener('mousedown', start)
            el.addEventListener('touchstart', start)
            if (shouldCancelOnTouchMove) {
                el.addEventListener('touchmove', cancelOnTouchMove, { passive: true })
            }
            el.addEventListener('click', cancel)
            el.addEventListener('mouseout', cancel)
            el.addEventListener('touchend', cancel)
            el.addEventListener('touchcancel', cancel)
            el.addEventListener('contextmenu', preventContextMenu)
        }
    })

    Vue.directive('press', {
        bind: function(el, binding) {
            function start(e) {
                // Solo actuar en click. No limpiar el long-press en touchend:
                // en touch, touchend llega antes que el click sintético y si
                // reseteamos aquí el click haría toggle (selecciona y deselecciona).
                if (e.type === 'touchend' || e.type === 'touchcancel') {
                    return
                }

                if (consumeTouchMoved(el)) {
                    return
                }

                if (consumeLongPressed(el)) {
                    return
                }

                binding.value(e)
            }

            el.addEventListener('click', start)
            el.addEventListener('touchend', start)
            el.addEventListener('touchcancel', start)
        }
    })
}

export default pressAndLongPress
